import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';

/**
 * PPE section landing: redirects to the first sub-page the user may access
 * (Detections before Summary), matching the Settings hub pattern.
 */
const PPE: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { canViewPPE, canViewPPESummary } = useRolePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation('pages', { keyPrefix: 'ppe' });

  const canAccessAnyPPE = canViewPPE || canViewPPESummary;

  useEffect(() => {
    if (userRolePermissionsLoading) return;
    if (!canAccessAnyPPE) return;

    const companyPrefix = currentCompany ? `/company/${currentCompany.company_id}` : '';

    if (canViewPPE) {
      navigate(`${companyPrefix}/ppe/detections`, { replace: true });
    } else if (canViewPPESummary) {
      navigate(`${companyPrefix}/ppe/summary`, { replace: true });
    }
  }, [
    userRolePermissionsLoading,
    canAccessAnyPPE,
    canViewPPE,
    canViewPPESummary,
    currentCompany,
    navigate,
  ]);

  return (
    <Layout title={t('title')}>
      <Container className="an-enterprise-page an-page-shell an-data-page">
        <div className="d-flex flex-column align-items-center justify-content-center an-panel p-5 mx-auto" style={{ maxWidth: 520 }} role="status" aria-live="polite">
          {userRolePermissionsLoading ? (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted mb-0">{t('loading')}</p>
            </>
          ) : !canAccessAnyPPE ? (
            <Alert variant="danger" role="alert" className="mb-0">
              {t('noAccess')}
            </Alert>
          ) : (
            <>
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted mb-0">{t('redirecting')}</p>
            </>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default PPE;
